/**
 * The typed view of an instance's capabilities. The backend's instance
 * manifest sends `capabilities` as an open record; feature code reads it
 * through `resolveCapabilities` (or `useCapability`) so each decision names
 * the capability it is about and an unknown or malformed value falls back to
 * the Ora default rather than switching a partner behaviour on.
 */
export interface InstanceCapabilities {
  /** The home experience: Ora's dashboard, or the partner course home. */
  homeVariant: 'default' | 'course';
  /** Whether Ora's study library (videos, QBank, flashcards, self-assessments) is offered. */
  studyLibrary: 'full' | 'none';
  /** Who sells access: Ora (billing, upgrades, referrals) or the partner. */
  commerce: 'ora' | 'partner';
  /** Games follow commerce by default; 'on' enables them for a partner. */
  games: 'auto' | 'on';
  /** A theme the instance imposes over the student's stored theme. */
  forcedTheme: 'none' | 'dark' | 'light';
  /** Whether the partner mark sits beside Ora's or replaces it. */
  brandMark: 'cobrand' | 'replace';
  /** Whether the student can change their email in Ora, or the partner owns it. */
  accountEmail: 'editable' | 'locked';
  /**
   * Whether the partner's LMS reaches Ora over LTI 1.3 (Connect Account, grade
   * status). The Connect landing itself is pre-auth and never reads this: the
   * backend refuses a launch for an instance without it.
   */
  lms: 'none' | 'lti';
}

export const DEFAULT_INSTANCE_CAPABILITIES: Readonly<InstanceCapabilities> =
  Object.freeze({
    homeVariant: 'default',
    studyLibrary: 'full',
    commerce: 'ora',
    games: 'auto',
    forcedTheme: 'none',
    brandMark: 'cobrand',
    accountEmail: 'editable',
    lms: 'none',
  });

const ALLOWED_VALUES: {
  [K in keyof InstanceCapabilities]: readonly InstanceCapabilities[K][];
} = {
  homeVariant: ['default', 'course'],
  studyLibrary: ['full', 'none'],
  commerce: ['ora', 'partner'],
  games: ['auto', 'on'],
  forcedTheme: ['none', 'dark', 'light'],
  brandMark: ['cobrand', 'replace'],
  accountEmail: ['editable', 'locked'],
  lms: ['none', 'lti'],
};

const readCapability = <K extends keyof InstanceCapabilities>(
  capabilities: Record<string, unknown>,
  key: K
): InstanceCapabilities[K] => {
  const value = capabilities[key];
  const allowed: readonly unknown[] = ALLOWED_VALUES[key];

  return allowed.includes(value)
    ? (value as InstanceCapabilities[K])
    : DEFAULT_INSTANCE_CAPABILITIES[key];
};

/**
 * Fills every known capability from the manifest record, falling back to the
 * default for a missing, unknown or invalid value. Keys the frontend does not
 * know are ignored.
 */
export const resolveCapabilities = (
  capabilities: Record<string, unknown> | null | undefined
): InstanceCapabilities => {
  const source = capabilities ?? {};

  return {
    homeVariant: readCapability(source, 'homeVariant'),
    studyLibrary: readCapability(source, 'studyLibrary'),
    commerce: readCapability(source, 'commerce'),
    games: readCapability(source, 'games'),
    forcedTheme: readCapability(source, 'forcedTheme'),
    brandMark: readCapability(source, 'brandMark'),
    accountEmail: readCapability(source, 'accountEmail'),
    lms: readCapability(source, 'lms'),
  };
};
